import { useMemo, useState } from "react";

import styled from "styled-components";

import { Dropdown, Icon, Tooltip } from "components/organisms/manuscript/adapter/visual/table-generator-ui";

import { useCodeMirrorViewContext } from "../codemirror-view-context.js";
import { useSelectionContext } from "../contexts/selection-context.js";
import { useTableContext } from "../contexts/table-context.js";
import { useTabularContext } from "../contexts/tabular-context.js";
import {
    BorderTheme,
    insertColumn,
    insertRow,
    mergeCells,
    moveCaption,
    removeCaption,
    removeColumnWidths,
    removeNodes,
    removeRowOrColumns,
    setAlignment,
    setBorders,
    unmergeCells,
} from "./commands.js";

const ToolbarContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin: 0 auto 12px;
    padding: 6px;
    width: fit-content;
    max-width: 100%;
    border-radius: 10px;
    background: var(--table-generator-toolbar-background);
    box-shadow: 0 2px 4px 0 var(--table-generator-toolbar-shadow-color);
`;

const Group = styled.div`
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding-right: 6px;
    margin-right: 2px;
    border-right: 1px solid var(--table-generator-divider-color);

    &:last-child {
        border-right: none;
        padding-right: 0;
        margin-right: 0;
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 34px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: ${(props) =>
        props.$active ? "var(--table-generator-toolbar-button-active-background)" : "transparent"};
    color: var(--table-generator-toolbar-button-color);
    font-size: 12px;
    font-weight: 600;
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    opacity: ${(props) => (props.disabled ? 0.48 : 1)};

    &:hover {
        background: ${(props) =>
            props.disabled
                ? props.$active
                    ? "var(--table-generator-toolbar-button-active-background)"
                    : "transparent"
                : "var(--table-generator-toolbar-button-hover-background)"};
    }
`;

const MenuPopover = styled.div`
    min-width: 180px;
    padding: 6px;
    border: 1px solid #d8e1ec;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
`;

const MenuItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 34px;
    padding: 0 10px;
    border: 0;
    border-radius: 8px;
    background: ${(props) => (props.$active ? "rgba(15, 143, 111, 0.12)" : "transparent")};
    color: ${(props) => (props.$active ? "#0f8f6f" : "#42576b")};
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(15, 143, 111, 0.08);
    }
`;

const Divider = styled.hr`
    margin: 6px 0;
    border: 0;
    border-top: 1px solid #e7edf4;
`;

function ToolbarIcon({ name, color }) {
    return <Icon name={name} size="13px" color={color || "var(--table-generator-toolbar-button-color)"} />;
}

function ToolbarButton({ label, icon, onClick, disabled = false, active = false, tooltip }) {
    const button = (
        <ActionButton
            type="button"
            $active={active}
            disabled={disabled}
            onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={disabled ? undefined : onClick}
        >
            {icon ? <ToolbarIcon name={icon} /> : null}
            {label ? <span>{label}</span> : null}
        </ActionButton>
    );

    return tooltip ? <Tooltip message={tooltip}>{button}</Tooltip> : button;
}

function ToolbarMenu({ trigger, items, width = "220px" }) {
    const [open, setOpen] = useState(false);
    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            offset={6}
            anchor={trigger(() => setOpen((prev) => !prev))}
            contentMinWidth={width}
            usePortal={false}
        >
            <MenuPopover>
                {items.map((item, index) =>
                    item.type === "divider" ? (
                        <Divider key={`divider-${index}`} />
                    ) : (
                        <MenuItem
                            key={item.id}
                            type="button"
                            $active={item.active}
                            disabled={item.disabled}
                            onClick={() => {
                                if (item.disabled) return;
                                item.onClick();
                                setOpen(false);
                            }}
                        >
                            <span>{item.label}</span>
                            {item.active ? <ToolbarIcon name="check" color="#0f8f6f" /> : null}
                        </MenuItem>
                    ),
                )}
            </MenuPopover>
        </Dropdown>
    );
}

export function Toolbar() {
    const { selection, setSelection } = useSelectionContext();
    const view = useCodeMirrorViewContext();
    const { positions, rowSeparators, cellSeparators, tableEnvironment, table, directTableChild } = useTableContext();
    const { showHelp, openColumnWidthModal } = useTabularContext();

    const captionPosition = useMemo(() => {
        if (!tableEnvironment?.caption) return "no_caption";
        if (tableEnvironment.caption.from < positions.tabular.from) return "above";
        return "below";
    }, [positions, tableEnvironment]);

    const currentAlignment = useMemo(() => {
        if (!selection) return undefined;
        if (selection.isMergedCellSelected(table)) {
            const cell = table.getCell(selection.from.row, selection.from.cell);
            return cell.multiColumn?.columns.specification[0]?.alignment;
        }
        const { minX, maxX } = selection.normalized();
        const alignment = table.columns[minX].alignment;
        for (let x = minX + 1; x <= maxX; x += 1) {
            if (table.columns[x].alignment !== alignment) {
                return undefined;
            }
        }
        return alignment;
    }, [selection, table]);

    if (!selection) {
        return null;
    }

    const columnsToInsert = selection.maximumCellWidth(table);
    const rowsToInsert = selection.height();
    const onlyFixedWidthColumnsSelected = selection.isOnlyFixedWidthColumns(table);
    const onlyNonFixedWidthColumnsSelected = selection.isOnlyNonFixedWidthColumns(table);

    const captionItems = [
        {
            id: "caption-none",
            label: "캡션 없음",
            active: captionPosition === "no_caption",
            onClick: () => removeCaption(view, tableEnvironment),
        },
        {
            id: "caption-above",
            label: "위쪽 캡션",
            active: captionPosition === "above",
            onClick: () => moveCaption(view, positions, "above", tableEnvironment),
        },
        {
            id: "caption-below",
            label: "아래쪽 캡션",
            active: captionPosition === "below",
            onClick: () => moveCaption(view, positions, "below", tableEnvironment),
        },
    ];

    const borderItems = [
        {
            id: "border-none",
            label: "테두리 없음",
            active: table.getBorderTheme() === BorderTheme.NO_BORDERS,
            onClick: () => setBorders(view, BorderTheme.NO_BORDERS, positions, rowSeparators, table),
        },
        {
            id: "border-all",
            label: "전체 테두리",
            active: table.getBorderTheme() === BorderTheme.FULLY_BORDERED,
            onClick: () => setBorders(view, BorderTheme.FULLY_BORDERED, positions, rowSeparators, table),
        },
        {
            id: "border-booktabs",
            label: "Booktabs",
            active: table.getBorderTheme() === BorderTheme.BOOKTABS,
            onClick: () => setBorders(view, BorderTheme.BOOKTABS, positions, rowSeparators, table),
        },
    ];

    const alignmentItems = [
        {
            id: "align-left",
            label: "왼쪽",
            active: currentAlignment === "left",
            onClick: () => setAlignment(view, selection, "left", positions, table),
        },
        {
            id: "align-center",
            label: "가운데",
            active: currentAlignment === "center",
            onClick: () => setAlignment(view, selection, "center", positions, table),
        },
        {
            id: "align-right",
            label: "오른쪽",
            active: currentAlignment === "right",
            onClick: () => setAlignment(view, selection, "right", positions, table),
        },
        ...(onlyFixedWidthColumnsSelected && !selection.isMergedCellSelected(table)
            ? [
                  {
                      id: "align-justify",
                      label: "양쪽 정렬",
                      active: currentAlignment === "paragraph",
                      onClick: () => setAlignment(view, selection, "paragraph", positions, table),
                  },
              ]
            : []),
    ];

    const widthItems = [
        {
            id: "width-auto",
            label: "내용에 맞게 너비 조정",
            active: onlyNonFixedWidthColumnsSelected,
            disabled: !selection.isAnyColumnSelected(table),
            onClick: () => removeColumnWidths(view, selection, positions, table),
        },
        {
            id: "width-fixed",
            label: onlyFixedWidthColumnsSelected ? "고정 너비" : "고정 너비 줄바꿈",
            active: onlyFixedWidthColumnsSelected,
            disabled: !selection.isAnyColumnSelected(table),
            onClick: openColumnWidthModal,
        },
        ...(onlyFixedWidthColumnsSelected
            ? [
                  { type: "divider" },
                  {
                      id: "width-set",
                      label: "열 너비 설정",
                      onClick: openColumnWidthModal,
                  },
              ]
            : []),
    ];

    const addItems = [
        {
            id: "insert-column-left",
            label: columnsToInsert === 1 ? "왼쪽에 열 삽입" : `왼쪽에 열 ${columnsToInsert}개 삽입`,
            onClick: () => setSelection(insertColumn(view, selection, positions, false, table)),
        },
        {
            id: "insert-column-right",
            label: columnsToInsert === 1 ? "오른쪽에 열 삽입" : `오른쪽에 열 ${columnsToInsert}개 삽입`,
            onClick: () => setSelection(insertColumn(view, selection, positions, true, table)),
        },
        { type: "divider" },
        {
            id: "insert-row-above",
            label: rowsToInsert === 1 ? "위에 행 삽입" : `위에 행 ${rowsToInsert}개 삽입`,
            onClick: () => setSelection(insertRow(view, selection, positions, false, rowSeparators, table)),
        },
        {
            id: "insert-row-below",
            label: rowsToInsert === 1 ? "아래에 행 삽입" : `아래에 행 ${rowsToInsert}개 삽입`,
            onClick: () => setSelection(insertRow(view, selection, positions, true, rowSeparators, table)),
        },
    ];

    const canAlign = selection.isColumnSelected(selection.from.cell, table) || selection.isMergedCellSelected(table);

    return (
        <ToolbarContainer className="table-generator-floating-toolbar">
            <Group>
                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label={
                                captionPosition === "no_caption"
                                    ? "캡션 없음"
                                    : captionPosition === "above"
                                      ? "위쪽 캡션"
                                      : "아래쪽 캡션"
                            }
                            icon="table"
                            onClick={toggle}
                            disabled={!tableEnvironment || !directTableChild}
                            tooltip="캡션"
                        />
                    )}
                    items={captionItems}
                />
                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label={
                                table.getBorderTheme() === BorderTheme.FULLY_BORDERED
                                    ? "전체 테두리"
                                    : table.getBorderTheme() === BorderTheme.BOOKTABS
                                      ? "Booktabs"
                                      : table.getBorderTheme() === BorderTheme.NO_BORDERS
                                        ? "테두리 없음"
                                        : "사용자 지정 테두리"
                            }
                            icon="table"
                            onClick={toggle}
                            tooltip="테두리"
                        />
                    )}
                    items={borderItems}
                />
            </Group>

            <Group>
                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label="정렬"
                            icon={
                                currentAlignment === "center"
                                    ? "alignCenter"
                                    : currentAlignment === "right"
                                      ? "alignRight"
                                      : "alignLeft"
                            }
                            onClick={toggle}
                            disabled={!canAlign}
                            tooltip="정렬"
                        />
                    )}
                    items={alignmentItems}
                />

                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label="너비"
                            icon="expand"
                            onClick={toggle}
                            disabled={!selection.isAnyColumnSelected(table)}
                            tooltip="열 너비 조정"
                        />
                    )}
                    items={widthItems}
                />

                <ToolbarButton
                    label={selection.isMergedCellSelected(table) ? "병합 해제" : "병합"}
                    icon="table"
                    onClick={() => {
                        if (selection.isMergedCellSelected(table)) {
                            unmergeCells(view, selection, table);
                        } else {
                            mergeCells(view, selection, table);
                        }
                    }}
                    disabled={!selection.isMergedCellSelected(table) && !selection.isMergeableCells(table)}
                    tooltip={selection.isMergedCellSelected(table) ? "셀 병합 해제" : "셀 병합"}
                />

                <ToolbarButton
                    label="삭제"
                    icon="delete"
                    onClick={() => setSelection(removeRowOrColumns(view, selection, positions, cellSeparators, table))}
                    disabled={
                        (!selection.isAnyRowSelected(table) && !selection.isAnyColumnSelected(table)) ||
                        !selection.eq(selection.explode(table))
                    }
                    tooltip="행 또는 열 삭제"
                />
            </Group>

            <Group>
                <ToolbarMenu
                    trigger={(toggle) => <ToolbarButton label="삽입" icon="plus" onClick={toggle} tooltip="삽입" />}
                    items={addItems}
                />
            </Group>

            <Group>
                <ToolbarButton
                    label="표 삭제"
                    icon="delete"
                    onClick={() => {
                        removeNodes(view, tableEnvironment?.table ?? positions.tabular);
                        view.focus();
                    }}
                    tooltip="표 삭제"
                />
                <ToolbarButton label="도움말" icon="question" onClick={showHelp} tooltip="도움말" />
            </Group>
        </ToolbarContainer>
    );
}
